import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Stack,
} from "@mui/material";
import DragIndicatorOutlinedIcon from "@mui/icons-material/DragIndicatorOutlined";
import { useData } from "../hooks/useData";

const RenameProjectDialog = ({ open, onClose, onRenameSuccess }) => {
  const { projectName, setProjectName } = useData();
  const [newName, setNewName] = useState(projectName || "");
  const [isRenamed, setIsRenamed] = useState(false);
  const [nameError, setNameError] = useState("");
  const [dialogPosition, setDialogPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Name validation
  const maxNameLength = 50;
  const nameRegex = /^[a-zA-Z0-9\s\-_]+$/;
  const validateName = (name) => {
    if (name.trim() === "") {
      return "Project name is required.";
    }
    if (name.length > maxNameLength) {
      return `Project name must be ${maxNameLength} characters or less.`;
    }
    if (!nameRegex.test(name)) {
      return "Project name can only contain letters, numbers, spaces, hyphens, and underscores.";
    }
    return "";
  };

  const isRenameDisabled = newName.trim() === "" || !!nameError;

  const handleNameChange = (e) => {
    const updatedName = e.target.value;
    setNewName(updatedName);
    setNameError(validateName(updatedName));
  };

  const handleRename = () => {
    if (isRenameDisabled) return;
    try {
      setProjectName(newName);
      setIsRenamed(true);
      onRenameSuccess?.();
    } catch (e) {
      console.error("Error renaming project:", e);
    }
  };

  const handleClose = () => {
    setNewName(projectName || "");
    setNameError("");
    setIsRenamed(false);
    setDialogPosition({ x: 0, y: 0 });
    onClose();
  };

  // Draggable functionality
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - dialogPosition.x,
      y: e.clientY - dialogPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setDialogPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          minWidth: 400,
          transform: `translate(${dialogPosition.x}px, ${dialogPosition.y}px)`,
        },
      }}
      aria-labelledby="dialog-title"
      maxWidth="sm"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <DialogTitle
        id="dialog-title"
        sx={{
          bgcolor: "primary.main",
          color: "white",
          fontSize: "1.25rem",
          cursor: "move",
          userSelect: "none",
          display: "flex",
          alignItems: "center",
          pb: 2,
        }}
        onMouseDown={handleMouseDown}
      >
        <DragIndicatorOutlinedIcon sx={{ fontSize: 20, mr: 1 }} />
        Rename Project
      </DialogTitle>
      <DialogContent sx={{ mt: 2, pt: 2, pb: 3 }}>
        {!isRenamed ? (
          <Stack direction="column" spacing={3}>
            <TextField
              id="projectName"
              label="Project Name"
              value={newName}
              onChange={handleNameChange}
              variant="outlined"
              size="medium"
              required
              fullWidth
              sx={{
                maxWidth: 350,
                mt: 2,
                pt: 1,
                "& .MuiInputLabel-root": {
                  fontSize: "1rem",
                  pt: 1,
                },
              }}
              error={!!nameError}
              helperText={nameError || "Enter a new name for your project"}
            />
            <DialogActions sx={{ justifyContent: "flex-end", px: 0 }}>
              <Button onClick={handleClose} color="secondary">
                Cancel
              </Button>
              <Button
                onClick={handleRename}
                disabled={isRenameDisabled}
                variant="contained"
                color="primary"
              >
                Rename
              </Button>
            </DialogActions>
          </Stack>
        ) : (
          <Stack direction="column" spacing={2} alignItems="center">
            <Typography variant="h6" color="success.main">
              Success
            </Typography>
            <Typography variant="body1">
              Your project has been renamed to "{newName}".
            </Typography>
            <Button
              onClick={handleClose}
              variant="contained"
              color="success"
              sx={{ mt: 1 }}
            >
              Close
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RenameProjectDialog;
